// src/services/tripMemberActions.ts
import { generateClient } from 'aws-amplify/api';
import { removeTripMemberWithAuth } from '../graphql/mutations';

const client = generateClient();

/**
 * Interface for member removal response
 */
export interface MemberRemovalResult {
  id: string;
  tripId: string;
  userId: string;
  role: string;
  photosDeleted: boolean;
  storageFreed: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Remove a member from a trip
 * @param memberId The ID of the member record to remove
 * @param tripId The ID of the trip (for validation)
 * @param deletePhotos Whether to also delete photos uploaded by the member
 */
export const removeTripMember = async (
  memberId: string, 
  tripId: string,
  deletePhotos: boolean = false
): Promise<MemberRemovalResult> => {
  try {
    // Use the secure Lambda function to remove the member
    const response: any = await client.graphql({
      query: removeTripMemberWithAuth,
      variables: { 
        memberId,
        deletePhotos
      },
      authMode: "userPool"
    });
    
    return response.data.removeTripMemberWithAuth;
  } catch (error) {
    console.error('Error removing trip member:', error);
    throw error;
  }
};

/**
 * Remove multiple members from a trip
 * @param memberIds Array of member IDs to remove
 * @param tripId The ID of the trip
 * @param deletePhotos Whether to also delete photos uploaded by the members
 */
export const removeMultipleTripMembers = async (
  memberIds: string[], 
  tripId: string,
  deletePhotos: boolean = false
): Promise<{
  successful: MemberRemovalResult[];
  failed: { id: string; error: any }[];
}> => {
  const successful: MemberRemovalResult[] = [];
  const failed: { id: string; error: any }[] = [];
  
  // Process each member removal sequentially
  for (const memberId of memberIds) {
    try {
      const result = await removeTripMember(memberId, tripId, deletePhotos);
      successful.push(result);
    } catch (error) {
      failed.push({ id: memberId, error });
    }
  }
  
  return { successful, failed };
};